import { CODE_SYSTEMS, LOINC_CODES } from '@liza/fhir-utils';
import type { PlanDefinition, PlanDefinitionAction, Timing } from '@medplum/fhirtypes';
import { every, itemKind, loincCode, patient, taskCategory } from './builders';

/**
 * The guideline this protocol is derived from. KDIGO states its monitoring cadences in practice
 * points and figures rather than numbered recommendations, so a citation names the locus it actually
 * sits in rather than asserting a recommendation number that does not exist.
 */
const KDIGO_CKD_2024 = {
  title: 'KDIGO 2024 Clinical Practice Guideline for the Evaluation and Management of CKD',
  url: 'https://kdigo.org/guidelines/ckd-evaluation-and-management/',
} as const;

export const CKD_ONGOING_MEASUREMENTS_URL = 'https://liza.health/fhir/PlanDefinition/ckd-ongoing-measurements';
export const CKD_ONGOING_MEASUREMENTS_VERSION = '1.0.0';
/** Version-pinned canonical, the value an expanded suggestion records as its provenance. */
export const CKD_ONGOING_MEASUREMENTS_CANONICAL = `${CKD_ONGOING_MEASUREMENTS_URL}|${CKD_ONGOING_MEASUREMENTS_VERSION}`;

/** The LOINC codes this protocol tracks, named from the shared `LOINC_CODES`. */
export const CKD_ONGOING_CODES = {
  EGFR: LOINC_CODES.EGFR,
  CREATININE: LOINC_CODES.CREATININE,
  URINE_ALBUMIN_CREATININE_RATIO: LOINC_CODES.URINE_ALBUMIN_CREATININE_RATIO,
  BICARBONATE: '1963-8',
} as const;

/**
 * The chronic kidney disease codes this protocol answers to. Both systems are listed for the same
 * reason as the diabetes protocol: a diagnosis reaches the chart in whichever system its source used,
 * and matching one alone makes the protocol invisible on a chart that plainly has the condition
 * (#10129).
 *
 * `N18` is an ICD-10 *prefix*, covering `N18.30`, `N18.4`, `N18.6` and the rest of the category, so a
 * chart staged to any severity matches. The SNOMED code is matched exactly.
 */
export const CKD_FOCUS_CODINGS = [
  { system: CODE_SYSTEMS.ICD10_CM, code: 'N18', display: 'Chronic kidney disease' },
  { system: CODE_SYSTEMS.SNOMED, code: '709044004', display: 'Chronic kidney disease' },
] as const;

/** Every action here is an unconditional patient-owned measurement; only code, cadence and words differ. */
const measurement = (
  id: string,
  title: string,
  description: string,
  code: string,
  /** Omitted where KDIGO asks for the result without naming an interval — see `bicarbonate`. */
  timing: Timing | undefined,
  locus: string,
): PlanDefinitionAction => ({
  id,
  title,
  description,
  code: loincCode(code),
  participant: patient,
  extension: [itemKind('measurement'), taskCategory('measurement')],
  ...(timing ? { timingTiming: timing } : {}),
  documentation: [{ type: 'citation', display: `${KDIGO_CKD_2024.title}, ${locus}`, url: KDIGO_CKD_2024.url }],
});

// --- actions ------------------------------------------------------------------------------------------

/**
 * KDIGO asks for eGFR and albuminuria together — the two axes of the risk grid — so both are tracked
 * on the same yearly floor. The grid itself raises the frequency to as often as four or more times a
 * year at higher stages; that is stage-conditional and is not expressed here.
 *
 * The guideline's wording is "at least annually" — a lower bound on frequency with no upper bound, so
 * a yearly period conforms. It is a floor rather than a fixed interval: measuring more often is
 * within the guideline, measuring less often is not.
 */
const egfr = measurement(
  'egfr',
  'eGFR',
  'Estimated glomerular filtration rate at least yearly.',
  CKD_ONGOING_CODES.EGFR,
  every(1, 'a'),
  'Practice Point 2.1.1',
);

const urineAlbuminCreatinineRatio = measurement(
  'urine-albumin-creatinine-ratio',
  'Urine albumin-creatinine ratio',
  'Urine albumin-creatinine ratio at least yearly.',
  CKD_ONGOING_CODES.URINE_ALBUMIN_CREATININE_RATIO,
  every(1, 'a'),
  'Practice Point 2.1.1',
);

/** eGFR is creatinine-derived, so it carries the same cadence rather than one of its own. */
const creatinine = measurement(
  'creatinine',
  'Creatinine',
  'Serum creatinine at least yearly, the value eGFR is calculated from.',
  CKD_ONGOING_CODES.CREATININE,
  every(1, 'a'),
  'Practice Point 2.1.1',
);

/**
 * No cadence: KDIGO asks that treated metabolic acidosis be monitored without naming an interval.
 * Materialized as an as-needed measurement so the result is on the plan, which is what the protocol
 * is for, without asserting a schedule the guideline does not give.
 */
const bicarbonate = measurement(
  'bicarbonate',
  'Bicarbonate',
  'Bicarbonate, as needed, where metabolic acidosis is being treated.',
  CKD_ONGOING_CODES.BICARBONATE,
  undefined,
  'Practice Point 3.10.2',
);

// --- the protocol -------------------------------------------------------------------------------------

/**
 * The measurements KDIGO asks for on an ongoing basis in chronic kidney disease, for a chart that has
 * the diagnosis. Every action is unconditional and patient-owned, matching the shape of
 * `t2dm-ongoing-measurements`.
 *
 * Only the yearly floor is here. KDIGO's monitoring grid (Figure 13) raises eGFR and albuminuria
 * monitoring to between one and four-or-more times a year depending on GFR stage and albuminuria
 * stage, and the mineral-and-bone and anemia cadences vary by stage in the same way. Those need
 * stage-indexed resolution the planner does not have, and a protocol that guessed a single stage would
 * under-monitor advanced disease. They arrive with the stage grid.
 *
 * Potassium is deliberately absent: KDIGO schedules it on starting a nonsteroidal MRA, not on the
 * diagnosis, so it belongs with the drug-conditional protocols. Bicarbonate is absent because KDIGO
 * asks that treated metabolic acidosis be monitored without naming an interval.
 */
export const ckdOngoingMeasurements: PlanDefinition = {
  resourceType: 'PlanDefinition',
  url: CKD_ONGOING_MEASUREMENTS_URL,
  version: CKD_ONGOING_MEASUREMENTS_VERSION,
  identifier: [{ system: 'urn:ietf:rfc:3986', value: CKD_ONGOING_MEASUREMENTS_URL }],
  name: 'ckd-ongoing-measurements',
  title: 'Chronic kidney disease — ongoing measurements',
  status: 'active',
  experimental: true,
  type: {
    coding: [
      {
        system: 'http://terminology.hl7.org/CodeSystem/plan-definition-type',
        code: 'clinical-protocol',
        display: 'Clinical Protocol',
      },
    ],
  },
  date: '2026-09-15',
  publisher: 'Liza Health',
  description:
    'Results recommended for ongoing monitoring in chronic kidney disease, expanded onto a care-plan topic when a chronic kidney disease Condition is linked to it.',
  useContext: [
    {
      code: { system: 'http://terminology.hl7.org/CodeSystem/usage-context-type', code: 'focus' },
      valueCodeableConcept: { coding: [...CKD_FOCUS_CODINGS] },
    },
  ],
  relatedArtifact: [{ type: 'derived-from', display: KDIGO_CKD_2024.title, url: KDIGO_CKD_2024.url }],
  action: [
    {
      id: 'ongoing-measurements',
      title: 'Ongoing measurements',
      description: 'Results tracked on an ongoing basis for chronic kidney disease.',
      action: [egfr, creatinine, urineAlbuminCreatinineRatio, bicarbonate],
    },
  ],
};
